import React, { useEffect, useMemo, useState } from "react";
import SummaryCards from "../../components/Common/SummaryCards";
import {
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../components/Common/AppOverlay";
import ActionMenu from "../../components/Common/ActionMenu";
import useAdminDataLifecycle from "../../components/Common/useAdminDataLifecycle";
import { showLoading } from "react-global-loading";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import { getUsersList } from "../../helpers/backend_helper";
import { postData } from "../../helpers/url-request";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import UserDetail from "./UserDetail";
import UserPassword from "./UserPassword";
import "../Standards/Standards.scss";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
};

const statusInfo = (status) =>
  status === 1
    ? { label: "Active", tone: "active" }
    : { label: "Inactive", tone: "archived" };

const UserActionMenu = ({
  user,
  onEdit,
  onResetPassword,
  onSetExternalPassword,
  onAlignProfileEmail,
  onProvisionExternalIdentity,
  lifecycleActions,
}) => {
  const loginName = user.userLogin?.loginName?.trim();
  const needsEmailAlignment =
    loginName && loginName.toLowerCase() !== user.emailId?.trim().toLowerCase();
  return (
    <ActionMenu
      label={user.fullName}
      actions={[
        {
          label: "Edit details",
          icon: <i className="bx bx-pencil" />,
          onSelect: () => onEdit(user),
        },
        needsEmailAlignment
          ? {
              label: "Use login as Entra email",
              icon: <i className="bx bx-envelope" />,
              onSelect: () => onAlignProfileEmail(user),
            }
          : null,
        {
          label: "Provision Entra identity",
          icon: <i className="bx bx-user-check" />,
          onSelect: () => onProvisionExternalIdentity(user),
        },
        {
          label: "Set Entra password",
          icon: <i className="bx bx-lock-alt" />,
          onSelect: () => onSetExternalPassword(user.id),
        },
        {
          label: "Reset legacy password",
          icon: <i className="bx bx-key" />,
          onSelect: () => onResetPassword(user.id),
        },
        ...lifecycleActions,
      ].filter(Boolean)}
    />
  );
};

const SettingsSysUsers = ({ createRequest = 0 }) => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [selectedMembershipFirmId, setSelectedMembershipFirmId] = useState(0);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isExternalPasswordOpen, setIsExternalPasswordOpen] = useState(false);
  const [drawerVersion, setDrawerVersion] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [firm, setFirm] = useState("all");

  const loadData = () => {
    showLoading(true);
    getUsersList()
      .then((response) => {
        if (response.status) setUsers(response.data || []);
        else ErrorNotify(response.message);
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify("Error occurred while fetching users.");
      })
      .finally(() => showLoading(false));
  };
  const lifecycle = useAdminDataLifecycle(loadData);

  const openDetail = (user = 0) => {
    setSelectedUserId(typeof user === "object" ? user.id : user);
    setSelectedMembershipFirmId(typeof user === "object" ? user.firmId : 0);
    setDrawerVersion((version) => version + 1);
    setIsDetailOpen(true);
  };

  const closeDetail = () => {
    setIsDetailOpen(false);
    setSelectedUserId(0);
    loadData();
  };

  const openPassword = (id) => {
    setSelectedUserId(id);
    setDrawerVersion((version) => version + 1);
    setIsPasswordOpen(true);
  };

  const closePassword = () => {
    setIsPasswordOpen(false);
    setSelectedUserId(0);
  };

  const openExternalPassword = (id) => {
    setSelectedUserId(id);
    setDrawerVersion((version) => version + 1);
    setIsExternalPasswordOpen(true);
  };

  const closeExternalPassword = () => {
    setIsExternalPasswordOpen(false);
    setSelectedUserId(0);
  };

  const alignProfileEmail = async (user) => {
    const loginName = user.userLogin?.loginName?.trim();
    if (!loginName)
      return ErrorNotify("This user does not have an active login email.");
    showLoading(true);
    try {
      const response = await postData("/user/updateuser", {
        userId: user.id,
        fullName: user.fullName,
        emailId: loginName,
        mobileNo: user.mobileNo,
        blockUser: user.isBlocked || 0,
        changePassword: user.changePassword || 0,
        disableUser: user.status === 1 ? 0 : 1,
      });
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to align the profile email."
        );
      SuccessNotify(
        `${user.fullName}'s Entra email now matches the PIERS login.`
      );
      loadData();
    } catch (error) {
      ErrorNotify(error.message || "Unable to align the profile email.");
    } finally {
      showLoading(false);
    }
  };

  const provisionExternalIdentity = async (user) => {
    showLoading(true);
    try {
      const response = await postData(`/user/provisionexternal/${user.id}`, {});
      if (!response?.status)
        throw new Error(
          response?.message || "External identity provisioning failed."
        );
      SuccessNotify(
        response.message === "already_provisioned"
          ? `${user.fullName} is already provisioned in Microsoft Entra.`
          : `${user.fullName} was provisioned in Microsoft Entra.`
      );
    } catch (error) {
      ErrorNotify(error.message || "External identity provisioning failed.");
    } finally {
      showLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    if (createRequest) openDetail();
  }, [createRequest]);

  const firms = useMemo(
    () =>
      Array.from(
        new Set(users.map((user) => user.firmDetail?.name).filter(Boolean))
      ),
    [users]
  );
  const counts = useMemo(
    () => ({
      total: users.length,
      active: users.filter((user) => user.status === 1).length,
      inactive: users.filter((user) => user.status !== 1).length,
      firms: firms.length,
    }),
    [users, firms]
  );
  const filteredUsers = useMemo(
    () =>
      users.filter((user) => {
        const searchText = `${user.fullName || ""} ${
          user.userLogin?.loginName || ""
        } ${user.firmDetail?.name || ""} ${user.role?.name || ""} ${
          user.designation || ""
        }`.toLowerCase();
        return (
          (!search || searchText.includes(search.toLowerCase())) &&
          (status === "all" || String(user.status) === status) &&
          (firm === "all" || user.firmDetail?.name === firm)
        );
      }),
    [users, search, status, firm]
  );

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setFirm("all");
  };

  const columns = [
    {
      id: "fullName",
      header: "User",
      accessorKey: "fullName",
      minSize: 185,
      flex: 1.1,
      cell: ({ row, getValue }) => (
        <button
          type="button"
          className="standards-grid__name"
          style={{ fontSize: 13 }}
          onClick={() => openDetail(row.original)}
        >
          <span>{(getValue() || "U").charAt(0)}</span>
          {getValue()}
        </button>
      ),
    },
    {
      id: "firm",
      header: "Firm",
      accessorFn: (row) => row.firmDetail?.name || "System",
      minSize: 135,
      flex: 0.8,
    },
    {
      id: "role",
      header: "User Role",
      accessorFn: (row) => row.role?.name || "—",
      minSize: 155,
      flex: 0.9,
    },
    {
      id: "branch",
      header: "Branch",
      accessorFn: (row) => row.branchDetail?.name || "—",
      minSize: 135,
      flex: 0.8,
    },
    {
      id: "designation",
      header: "Designation",
      accessorKey: "designation",
      minSize: 140,
      flex: 0.85,
    },
    {
      id: "login",
      header: "Login ID",
      accessorFn: (row) => row.userLogin?.loginName || row.loginName || "—",
      minSize: 175,
      flex: 1,
    },
    {
      id: "lastLogin",
      header: "Last Login",
      accessorFn: (row) => formatDate(row.lastLogin),
      minSize: 135,
      flex: 0.8,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => statusInfo(row.status).label,
      minSize: 105,
      flex: 0.65,
      cell: ({ row, getValue }) => (
        <span
          className={`standards-grid__status standards-grid__status--${
            statusInfo(row.original.status).tone
          }`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      minSize: 80,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      cell: ({ row }) => (
        <UserActionMenu
          user={row.original}
          onEdit={openDetail}
          onResetPassword={openPassword}
          onSetExternalPassword={openExternalPassword}
          onAlignProfileEmail={alignProfileEmail}
          onProvisionExternalIdentity={provisionExternalIdentity}
          lifecycleActions={lifecycle.actions("user", row.original)}
        />
      ),
    },
  ];

  return (
    <section className="standards-page__content">
      <SummaryCards
        items={[
          [
            "bx-group",
            "blue",
            "Total Users",
            counts.total,
            "All platform users",
          ],
          [
            "bx-check-circle",
            "green",
            "Active Users",
            counts.active,
            `${
              counts.total
                ? Math.round((counts.active / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-pause-circle",
            "orange",
            "Inactive Users",
            counts.inactive,
            `${
              counts.total
                ? Math.round((counts.inactive / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-buildings",
            "red",
            "Firms Represented",
            counts.firms,
            "Firms with active users",
          ],
        ]}
        ariaLabel="Page summary"
      />
      <>
        
        <CustomDataGrid
 filterContent={<section className="standards-filter-panel border-bottom-0 pt-0" aria-label="User filters">
          <div className="standards-filter-panel__fields">
            <label>
              <span>Search</span>
              <div className="standards-filter-panel__search">
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search users..."
                />
              </div>
            </label>
            <label>
              <span>Firm</span>
              <select
                value={firm}
                onChange={(event) => setFirm(event.target.value)}
              >
                <option value="all">All Firms</option>
                {firms.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </label>
          </div>
          <div className="standards-filter-panel__actions">
            <button type="button" onClick={resetFilters}>Clear</button>
          </div>
        </section>}

          data={filteredUsers}
          columns={columns}
          onRefresh={loadData}
          fileName="users"
          recordLabel="users"
          showToolbar={false}
          initialPageSize={15}
          rowHeight={56}
          columnHeaderHeight={56}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e7ebf2",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              { backgroundColor: "#ffffff !important", color: "#52627f" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".2px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              fontSize: 13,
              borderBottomColor: "#edf0f5",
              color: "#273957",
            },
            "& .MuiDataGrid-row": { minHeight: "56px !important" },
            "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
              { fontSize: 14, borderTopColor: "#e7ebf2" },
          }}
        />
      </>
      <Offcanvas
        backdrop="static"
        className="standards-drawer standards-drawer--wide"
        direction="end"
        isOpen={isDetailOpen}
        toggle={closeDetail}
      >
        <OffcanvasHeader toggle={closeDetail} className="canvasHeader">
          {selectedUserId ? "Edit User" : "New User"}
        </OffcanvasHeader>
        <OffcanvasBody>
          <UserDetail
            key={`${selectedUserId}-${drawerVersion}`}
            userId={selectedUserId}
            firmId={selectedMembershipFirmId}
            closeAction={closeDetail}
            onEditUser={openDetail}
          />
        </OffcanvasBody>
      </Offcanvas>
      <Offcanvas
        backdrop="static"
        className="standards-drawer"
        direction="end"
        isOpen={isPasswordOpen}
        toggle={closePassword}
      >
        <OffcanvasHeader toggle={closePassword} className="canvasHeader">
          Reset Password
        </OffcanvasHeader>
        <OffcanvasBody>
          <UserPassword
            key={`${selectedUserId}-${drawerVersion}`}
            userId={selectedUserId}
            closeAction={closePassword}
          />
        </OffcanvasBody>
      </Offcanvas>
      <Offcanvas
        backdrop="static"
        className="standards-drawer"
        direction="end"
        isOpen={isExternalPasswordOpen}
        toggle={closeExternalPassword}
      >
        <OffcanvasHeader
          toggle={closeExternalPassword}
          className="canvasHeader"
        >
          Set Entra Password
        </OffcanvasHeader>
        <OffcanvasBody>
          <UserPassword
            key={`entra-${selectedUserId}-${drawerVersion}`}
            userId={selectedUserId}
            closeAction={closeExternalPassword}
            external
          />
        </OffcanvasBody>
      </Offcanvas>
      {lifecycle.dialog}
    </section>
  );
};

export default SettingsSysUsers;
